import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useMemo } from 'react';
import { parseISO, getYear } from 'date-fns';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatSignedDollars = (value) => {
	const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(value || 0));
	return (value || 0) >= 0 ? `+${formatted}` : `-${formatted}`;
};

export default function MonthlyPnlTiles({ data }) {
	const [currentYear, setCurrentYear] = useState(() => {
		if (!data || data.length === 0) return new Date().getFullYear();
		const lastDate = parseISO(data[data.length - 1].date);
		return getYear(lastDate);
	});

	const monthlyData = useMemo(() => {
		if (!data || data.length === 0) return {};

		const monthMap = {};
		data.forEach((d) => {
			const date = parseISO(d.date);
			if (getYear(date) !== currentYear) return;
			const month = date.getMonth();
			if (!monthMap[month]) {
				monthMap[month] = { pnl: 0, trades: 0, days: 0 };
			}
			monthMap[month].pnl += d.profit || 0;
			monthMap[month].trades += d.trades || 0;
			monthMap[month].days += 1;
		});

		return monthMap;
	}, [data, currentYear]);

	const maxPnl = useMemo(() => {
		const values = Object.values(monthlyData).map((m) => Math.abs(m.pnl));
		if (values.length === 0) return 1;
		return Math.max(...values, 1);
	}, [monthlyData]);

	const yearStats = useMemo(() => {
		let totalPnl = 0;
		let totalTrades = 0;
		let months = 0;
		for (const m of Object.values(monthlyData)) {
			totalPnl += m.pnl;
			totalTrades += m.trades;
			months++;
		}
		return { totalPnl, totalTrades, months };
	}, [monthlyData]);

	if (!data || data.length === 0) {
		return (
			<Card>
				<CardHeader><CardTitle className="text-sm">Monthly P&L</CardTitle></CardHeader>
				<CardContent>
					<div className="text-muted-foreground flex min-h-[200px] items-center justify-center text-sm">No data available</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<CardTitle className="text-sm">Monthly P&L</CardTitle>
					<div className="flex items-center gap-3">
						<span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${yearStats.totalPnl >= 0 ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}`}>
							{formatSignedDollars(yearStats.totalPnl)}
						</span>
						<span className="text-muted-foreground text-xs">{yearStats.totalTrades}T</span>
						<span className="text-muted-foreground text-xs">{yearStats.months} months</span>
					</div>
				</div>
				<div className="mt-2 flex items-center gap-2">
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentYear((y) => y - 1)}>
						<ChevronLeft className="h-4 w-4" />
					</Button>
					<span className="min-w-[60px] text-center text-sm font-medium">{currentYear}</span>
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentYear((y) => y + 1)}>
						<ChevronRight className="h-4 w-4" />
					</Button>
					<Button variant="outline" size="sm" className="ml-1 h-7 text-xs" onClick={() => setCurrentYear(new Date().getFullYear())}>
						This Year
					</Button>
				</div>
			</CardHeader>
			<CardContent>
				<div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
					{MONTH_NAMES.map((name, i) => {
						const m = monthlyData[i];

						if (!m) {
							return (
								<div key={i} className="flex flex-col items-center justify-center rounded bg-muted/30 p-3">
									<span className="text-muted-foreground text-xs font-medium">{name}</span>
									<span className="text-muted-foreground mt-1 text-[10px]">No data</span>
								</div>
							);
						}

						const intensity = Math.min(1, Math.abs(m.pnl) / maxPnl);
						const isPositive = m.pnl >= 0;
						const bgColor = isPositive
							? `rgba(34, 197, 94, ${0.12 + intensity * 0.5})`
							: `rgba(239, 68, 68, ${0.12 + intensity * 0.5})`;

						return (
							<div
								key={i}
								className="flex flex-col items-center justify-center rounded p-3"
								style={{ background: bgColor }}
								title={`${name} ${currentYear}: ${formatSignedDollars(m.pnl)} (${m.trades} trades, ${m.days} days)`}
							>
								<span className="text-foreground/60 text-xs font-medium">{name}</span>
								<span className="mt-0.5 text-sm font-bold text-foreground/90">{formatSignedDollars(m.pnl)}</span>
								<span className="text-foreground/50 text-[10px]">{m.trades}T</span>
								<span className="text-foreground/50 text-[9px]">{m.days} days</span>
							</div>
						);
					})}
				</div>
			</CardContent>
		</Card>
	);
}
